import { useEffect, useMemo, useState } from "react";
import { api } from "./adminApi";

const API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : window.location.origin);


const COLORS = {
  text: "#0f172a",
  sub: "#344054",
  muted: "#667085",
  border: "#e6eaf0",
  bg: "#f6f7fb",
  card: "#ffffff",
};

const FW = {
  pageTitle: 900,
  sectionTitle: 900,
  body: 600,
  muted: 600,
  kpiValue: 900,
  btn: 800,
  tableHead: 800,
};

const KPI_THEMES = {
  total: {
    title: "Total Submissions",
    gradient: "linear-gradient(135deg,#2563eb 0%,#60a5fa 100%)",
    icon: "∑",
  },
  approved: {
    title: "Approved",
    gradient: "linear-gradient(135deg,#16a34a 0%,#34d399 100%)",
    icon: "✓",
  },
  pending: {
    title: "Pending",
    gradient: "linear-gradient(135deg,#f59e0b 0%,#fbbf24 100%)",
    icon: "⏳",
  },
  rejected: {
    title: "Rejected",
    gradient: "linear-gradient(135deg,#ef4444 0%,#fb7185 100%)",
    icon: "✕",
  },
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [query]);
  return matches;
}

function Notice({ tone = "info", children }) {
  const cfg =
    tone === "danger"
      ? { bg: "#fff1f2", bd: "#fecdd3", fg: "#7f1d1d" }
      : { bg: "#eff6ff", bd: "#bfdbfe", fg: "#0f172a" };
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 14,
        background: cfg.bg,
        border: `1px solid ${cfg.bd}`,
        fontWeight: 800,
        color: cfg.fg,
      }}
    >
      {children}
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 34,
        padding: "0 12px",
        borderRadius: 999,
        border: "1px solid #d0d5dd",
        background: active ? "#111827" : "#fff",
        color: active ? "#fff" : "#111827",
        cursor: "pointer",
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function pillBg(s) {
  const v = (s || "pending").toLowerCase();
  if (v === "approved") return "#ecfdf3";
  if (v === "rejected") return "#fef3f2";
  return "#fff6ed";
}
function pillColor(s) {
  const v = (s || "pending").toLowerCase();
  if (v === "approved") return "#027a48";
  if (v === "rejected") return "#b42318";
  return "#b54708";
}

function KpiCard({ theme, value }) {
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 18,
        padding: 12,
        background: theme.gradient,
        boxShadow: "0 14px 34px rgba(16,24,40,0.10)",
        minHeight: 96,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            color: "#fff",
            fontWeight: 900,
            border: "1px solid rgba(255,255,255,0.25)",
            background: "rgba(255,255,255,0.16)",
            flex: "0 0 auto",
          }}
        >
          {theme.icon}
        </div>
        <div style={{ color: "rgba(255,255,255,0.95)", fontWeight: 900, fontSize: 13 }}>{theme.title}</div>
      </div>

      <div style={{ marginTop: 10, fontSize: 28, fontWeight: FW.kpiValue, color: "#fff", letterSpacing: "-0.4px" }}>
        {Number(value || 0)}
      </div>

      <div
        style={{
          position: "absolute",
          right: -36,
          bottom: -38,
          width: 120,
          height: 120,
          borderRadius: 999,
          background: "rgba(255,255,255,0.16)",
          filter: "blur(1px)",
        }}
      />
    </div>
  );
}

/* ---------------- Media helpers ---------------- */

function getExtFromUrl(u = "") {
  try {
    const clean = String(u).split("?")[0].split("#")[0];
    const parts = clean.split(".");
    if (parts.length < 2) return "";
    return parts[parts.length - 1].toLowerCase();
  } catch {
    return "";
  }
}

function guessMimeByExt(ext) {
  if (!ext) return "";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "mov") return "video/quicktime";
  if (ext === "mp3") return "audio/mpeg";
  if (ext === "wav") return "audio/wav";
  if (ext === "m4a") return "audio/mp4";
  if (ext === "ogg") return "audio/ogg";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "gif") return "image/gif";
  return "";
}

function pickIconForType(t = "") {
  const v = String(t || "").toUpperCase();
  if (v === "VIDEO") return "🎞️";
  if (v === "AUDIO") return "🔊";
  if (v === "IMAGE") return "🖼️";
  if (v === "LINK") return "🔗";
  return "📎";
}

function fileNameFromMedia(m) {
  if (m?.original_name) return m.original_name;
  const url = String(m?.url || "");
  const clean = url.split("?")[0];
  const seg = clean.split("/").filter(Boolean);
  return seg[seg.length - 1] || "download";
}

function bytesToSize(bytes) {
  const b = Number(bytes || 0);
  if (!b) return "";
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

/* ---------------- Media Modal ---------------- */

function MediaModal({ open, onClose, row, toAbs }) {
  if (!open || !row) return null;
  const media = Array.isArray(row.media) ? row.media : [];

  return (
    <div style={styles.modalOverlay} onMouseDown={onClose}>
      <div style={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.modalTitle}>
              Media Preview — <span style={{ color: COLORS.sub }}>{row.full_name || "-"}</span>
            </div>
            <div style={styles.modalSub}>
              {row.created_at ? new Date(row.created_at).toLocaleString() : "-"} •{" "}
              <span style={{ ...styles.pill, background: pillBg(row.status), color: pillColor(row.status) }}>
                {row.status || "pending"}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={styles.modalCloseBtn} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.modalTextBox}>
            <div style={styles.modalTextTitle}>Contribution Text</div>
            <div style={styles.modalText}>{row.contribution_text || "-"}</div>
          </div>

          <div style={styles.modalMediaBox}>
            <div style={styles.modalTextTitle}>Uploaded Media ({media.length})</div>

            {media.length === 0 ? (
              <div style={styles.emptyMedia}>No media uploaded for this contribution.</div>
            ) : (
              <div style={styles.mediaGrid}>
                {media.map((m) => {
                  const mediaType = String(m.media_type || "").toUpperCase();
                  const absUrl = toAbs(m.url);
                  const ext = getExtFromUrl(absUrl);
                  const mime = guessMimeByExt(ext);
                  const name = fileNameFromMedia(m);
                  const size = bytesToSize(m.size_bytes);

                  return (
                    <div key={m.id || absUrl} style={styles.mediaItem}>
                      <div style={styles.mediaTop}>
                        <div style={styles.mediaName} title={name}>
                          <span style={{ marginRight: 8 }}>{pickIconForType(mediaType)}</span>
                          {name}
                        </div>
                        <div style={styles.mediaMeta}>
                          {mediaType || "MEDIA"} {size ? `• ${size}` : ""}
                        </div>
                      </div>

                      {mediaType === "IMAGE" ? (
                        <img
                          src={absUrl}
                          alt={name}
                          style={styles.previewImage}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : mediaType === "AUDIO" ? (
                        <audio controls style={styles.previewAudio}>
                          <source src={absUrl} type={mime || undefined} />
                          Your browser does not support the audio element.
                        </audio>
                      ) : mediaType === "VIDEO" ? (
                        <video controls playsInline style={styles.previewVideo}>
                          <source src={absUrl} type={mime || "video/mp4"} />
                          Your browser does not support the video element.
                        </video>
                      ) : mediaType === "LINK" ? (
                        <a href={absUrl} target="_blank" rel="noreferrer" style={styles.linkPreview}>
                          Open Link
                        </a>
                      ) : (
                        <a href={absUrl} target="_blank" rel="noreferrer" style={styles.linkPreview}>
                          Open File
                        </a>
                      )}

                      <div style={styles.mediaActions}>
                        <a href={absUrl} target="_blank" rel="noreferrer" style={styles.ghostActionBtn}>
                          Open
                        </a>
                        <a href={absUrl} download style={styles.primaryActionBtn}>
                          Download
                        </a>
                      </div>

                      <div style={styles.mediaHint}>
                        If video has no sound: the MP4 may be silent or has unsupported audio codec.
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.modalDoneBtn}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const isTablet = useMediaQuery("(max-width: 960px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [persons, setPersons] = useState([]);
  const [personId, setPersonId] = useState(() => localStorage.getItem("activePersonId") || "");
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [recent, setRecent] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  const selectedPerson = useMemo(() => {
    const idNum = Number(personId);
    return persons.find((x) => x.id === idNum) || persons[0] || null;
  }, [persons, personId]);

  const publicUrl = useMemo(() => {
    if (!selectedPerson?.slug) return "";
    return `${window.location.origin}/p/${selectedPerson.slug}`;
  }, [selectedPerson]);

  const crumbs = useMemo(() => {
    const p = selectedPerson?.name_en || selectedPerson?.name_kn || "";
    return p ? `Admin / Dashboard • ${p}` : "Admin / Dashboard";
  }, [selectedPerson]);

  const filtered = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    return (recent || [])
      .filter((r) => (tab === "all" ? true : (r.status || "").toLowerCase() === tab))
      .filter((r) => {
        if (!s) return true;
        const name = (r.full_name || "").toLowerCase();
        const mob = (r.mobile || "").toLowerCase();
        const txt = (r.contribution_text || "").toLowerCase();
        return name.includes(s) || mob.includes(s) || txt.includes(s);
      });
  }, [recent, tab, q]);

  // ✅ UPDATED: validate localStorage activePersonId after loading persons
  useEffect(() => {
    (async () => {
      try {
        setErr("");

        // Cache-bust + no-cache headers to avoid 304 after DB resets
        const res = await api.get("/api/admin/persons", {
          params: { _ts: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });

        const list = res.data || [];
        setPersons(list);

        // If localStorage has an old personId (e.g., after truncating tables),
        // fall back to the first available person.
        const stored = localStorage.getItem("activePersonId") || "";
        const exists = stored && list.some((p) => String(p.id) === String(stored));
        const picked = exists ? String(stored) : list.length ? String(list[0].id) : "";

        if (picked) {
          setPersonId(picked);
          localStorage.setItem("activePersonId", picked);
        } else {
          setPersonId("");
          localStorage.removeItem("activePersonId");
        }
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load persons");
        setPersons([]);
        setPersonId("");
        localStorage.removeItem("activePersonId");
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!personId) return;
    loadDashboard(personId);
    // eslint-disable-next-line
  }, [personId]);

  async function loadDashboard(pid) {
    try {
      setLoading(true);
      setErr("");

      // ✅ UPDATED: cache bust + no-cache headers to reduce 304
      const res = await api.get("/api/admin/dashboard", {
        params: { personId: pid, _ts: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });

      setStats(res.data?.stats || { total: 0, approved: 0, pending: 0, rejected: 0 });
      setRecent(res.data?.recent || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load dashboard");
      setStats({ total: 0, approved: 0, pending: 0, rejected: 0 });
      setRecent([]);
    } finally {
      setLoading(false);
    }
  }

  function toAbs(u) {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    return u.startsWith("/") ? `${API}${u}` : `${API}/${u}`;
  }

  async function copyLink() {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    toast("Public link copied.");
  }

  function toast(msg) {
    const el = document.createElement("div");
    el.textContent = msg;
    el.style.position = "fixed";
    el.style.right = "14px";
    el.style.bottom = "14px";
    el.style.padding = "10px 12px";
    el.style.borderRadius = "12px";
    el.style.background = "rgba(15,23,42,0.92)";
    el.style.color = "white";
    el.style.fontWeight = "600";
    el.style.fontSize = "13px";
    el.style.boxShadow = "0 14px 36px rgba(0,0,0,0.25)";
    el.style.zIndex = 9999;
    el.style.maxWidth = "76vw";
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.transition = "opacity 200ms ease";
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 220);
    }, 1200);
  }

  function openPreview(row) {
    setPreviewRow(row);
    setPreviewOpen(true);
  }

  function closePreview() {
    setPreviewOpen(false);
    setPreviewRow(null);
  }

function downloadPdf() {
  const esc = (v) => {
    const s = String(v ?? "");
    return s
      .split("&").join("&amp;")
      .split("<").join("&lt;")
      .split(">").join("&gt;")
      .split('"').join("&quot;")
      .split("'").join("&#039;");
  };

  // Absolute URL for images inside print iframe
  const abs = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return s.startsWith("/") ? `${API}${s}` : `${API}/${s}`;
  };

  const personName = selectedPerson?.name_en || selectedPerson?.name_kn || "Admin Dashboard";
  const stamp = new Date().toLocaleString();

  // Build rows with Photos column (images only)
  const rowsHtml = (filtered || [])
    .map((r, i) => {
      const created = r.created_at ? new Date(r.created_at).toLocaleString() : "-";

      const mediaArr = Array.isArray(r.media) ? r.media : [];
      const imagesAll = mediaArr.filter(
        (m) => String(m?.media_type || "").toUpperCase() === "IMAGE" && m?.url
      );

      // Keep PDF light: show max 3 images per row
      const images = imagesAll.slice(0, 3);
      const moreCount = Math.max(0, imagesAll.length - images.length);

      const photosHtml = images.length
        ? `
          <div class="imgs">
            ${images
              .map((m) => `<img class="img" src="${esc(abs(m.url))}" alt="photo" />`)
              .join("")}
          </div>
          ${moreCount ? `<div class="m">+${moreCount} more</div>` : ""}
        `
        : `<span class="m">-</span>`;

      return `
        <tr>
          <td class="c">${i + 1}</td>
          <td>
            <div class="b">${esc(r.full_name || "-")}</div>
            <div class="m">${esc(r.mobile || "")}</div>
          </td>
          <td class="mono">${esc(r.content_lang || "-")}</td>
          <td>
            <span class="pill ${(r.status || "pending").toLowerCase()}">${esc(r.status || "pending")}</span>
          </td>
          <td class="m">${esc(created)}</td>
          <td class="wrap">${esc(r.contribution_text || "-")}</td>
          <td>${photosHtml}</td>
        </tr>
      `;
    })
    .join("");

  const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${esc(personName)} - Report</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; }
    .top { border: 1px solid #e6eaf0; border-radius: 14px; padding: 12px; margin-bottom: 12px; }
    .h1 { font-size: 18px; font-weight: 700; margin: 0; }
    .sub { color: #667085; font-size: 12px; margin-top: 6px; }
    .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 10px; }
    .kpi { border: 1px solid #e6eaf0; border-radius: 12px; padding: 10px; }
    .kpi .t { font-size: 11px; color: #667085; font-weight: 600; }
    .kpi .v { margin-top: 6px; font-size: 16px; font-weight: 700; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px; }
    .meta .box { border: 1px solid #e6eaf0; border-radius: 12px; padding: 10px; }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #e6eaf0; padding: 8px; vertical-align: top; }
    th { background: #f8fafc; text-align: left; font-size: 12px; color: #344054; }
    td { font-size: 12px; }
    .c { text-align: center; width: 40px; }
    .b { font-weight: 700; }
    .m { color: #667085; font-size: 11px; margin-top: 4px; }
    .wrap { white-space: pre-wrap; }
    .pill { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; border: 1px solid #e6eaf0; }
    .pill.approved { background: #ecfdf3; color: #027a48; }
    .pill.rejected { background: #fef3f2; color: #b42318; }
    .pill.pending { background: #fff6ed; color: #b54708; }

    /* Photos */
    .imgs { display: flex; gap: 6px; flex-wrap: wrap; }
    .img  { width: 72px; height: 48px; object-fit: cover; border-radius: 8px; border: 1px solid #e6eaf0; background: #f2f4f7; }
  </style>
</head>
<body>
  <div class="top">
    <p class="h1">${esc(personName)}</p>
    <div class="sub">Generated: ${esc(stamp)}</div>

    <div class="meta">
      <div class="box">
        <div class="t" style="font-size:11px;color:#667085;font-weight:600;">Public Link</div>
        <div class="mono" style="margin-top:6px;word-break:break-all;">${esc(publicUrl || "-")}</div>
      </div>
      <div class="box">
        <div class="t" style="font-size:11px;color:#667085;font-weight:600;">Filters</div>
        <div style="margin-top:6px;font-size:12px;">
          Status: <b>${esc(tab)}</b><br/>
          Search: <b>${esc(q || "-")}</b>
        </div>
      </div>
    </div>

    <div class="kpis">
      <div class="kpi"><div class="t">Total</div><div class="v">${Number(stats.total || 0)}</div></div>
      <div class="kpi"><div class="t">Approved</div><div class="v">${Number(stats.approved || 0)}</div></div>
      <div class="kpi"><div class="t">Pending</div><div class="v">${Number(stats.pending || 0)}</div></div>
      <div class="kpi"><div class="t">Rejected</div><div class="v">${Number(stats.rejected || 0)}</div></div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:40px;">#</th>
        <th>Contributor</th>
        <th style="width:90px;">Lang</th>
        <th style="width:90px;">Status</th>
        <th style="width:140px;">Date</th>
        <th>Contribution Text</th>
        <th style="width:190px;">Photos</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || `<tr><td colspan="7" style="text-align:center;color:#667085;">No records for current filter.</td></tr>`}
    </tbody>
  </table>
</body>
</html>`;

  // ✅ Print via hidden iframe
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!doc || !win) {
    iframe.remove();
    toast("Unable to open print preview.");
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  // ✅ Wait for images to load (or error) before printing,
  // otherwise PDF will capture blank boxes.
  const waitForImages = (timeoutMs = 8000) => {
    const imgs = Array.from(win.document.images || []);
    if (!imgs.length) return Promise.resolve();

    return new Promise((resolve) => {
      let done = 0;
      const finish = () => {
        done += 1;
        if (done >= imgs.length) resolve();
      };

      const t = setTimeout(() => resolve(), timeoutMs);

      imgs.forEach((img) => {
        if (img.complete) return finish();
        img.onload = () => finish();
        img.onerror = () => finish();
      });

      if (imgs.every((im) => im.complete)) {
        clearTimeout(t);
        resolve();
      }
    });
  };

  setTimeout(async () => {
    try {
      await waitForImages(10000); // 10s max
      win.focus();
      win.print();
    } finally {
      setTimeout(() => iframe.remove(), 2500);
    }
  }, 200);
}



  const heroGridStyle = {
    ...styles.heroGrid,
    gridTemplateColumns: isTablet ? "1fr" : "1fr auto",
  };

  const heroControlsStyle = {
    ...styles.heroControls,
    gridTemplateColumns: isMobile ? "1fr" : "220px 1fr",
  };

  return (
    <div style={styles.page}>
      <MediaModal open={previewOpen} onClose={closePreview} row={previewRow} toAbs={toAbs} />

      <div style={styles.headerRow}>
        <div>
          <div style={styles.pageTitle}>Admin Dashboard</div>
          <div style={styles.breadcrumb}>{crumbs}</div>
        </div>
      </div>

      <div style={styles.heroCard}>
        <div style={heroGridStyle}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            <div style={styles.personPhoto}>
              {selectedPerson?.photo_url ? (
                <img
                  alt="person"
                  src={toAbs(selectedPerson.photo_url)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ fontWeight: 900, color: COLORS.text }}>
                  {(selectedPerson?.name_en || "P").slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={styles.personName}>{selectedPerson?.name_en || selectedPerson?.name_kn || "Select Person"}</div>
              <div style={styles.personSub}>Review and publish approved content for this profile.</div>

              <div style={heroControlsStyle}>
                <select
                  value={personId || ""}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    setPersonId(v);
                    localStorage.setItem("activePersonId", v);
                  }}
                  style={styles.select}
                >
                  {persons.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name_en || p.name_kn || p.slug}
                    </option>
                  ))}
                </select>

                <div style={styles.linkBox}>
                  <div style={styles.linkText} title={publicUrl}>
                    {publicUrl || "—"}
                  </div>
                  <button
                    onClick={copyLink}
                    disabled={!publicUrl}
                    style={{ ...styles.smallBtn, opacity: publicUrl ? 1 : 0.55 }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={downloadPdf}
            style={{
              ...styles.primaryBtn,
              justifySelf: isTablet ? "start" : "end",
              width: isTablet ? "100%" : "auto",
              maxWidth: isTablet ? "100%" : 260,
            }}
          >
            Download PDF
          </button>
        </div>
      </div>

      <div style={styles.kpiGrid}>
        <KpiCard theme={KPI_THEMES.total} value={stats.total} />
        <KpiCard theme={KPI_THEMES.approved} value={stats.approved} />
        <KpiCard theme={KPI_THEMES.pending} value={stats.pending} />
        <KpiCard theme={KPI_THEMES.rejected} value={stats.rejected} />
      </div>

      <div style={styles.sectionRow}>
        <div style={styles.sectionTitle}>Recent Contributions</div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Tab active={tab === "all"} onClick={() => setTab("all")}>
            All
          </Tab>
          <Tab active={tab === "pending"} onClick={() => setTab("pending")}>
            Pending
          </Tab>
          <Tab active={tab === "approved"} onClick={() => setTab("approved")}>
            Approved
          </Tab>
          <Tab active={tab === "rejected"} onClick={() => setTab("rejected")}>
            Rejected
          </Tab>
        </div>

        <div style={{ flex: "1 1 220px" }} />

        <div style={styles.searchWrap}>
          <span style={{ opacity: 0.55 }}>🔎</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contributor or text." style={styles.search} />
        </div>
      </div>

      {err ? <Notice tone="danger">{err}</Notice> : null}

      {loading ? (
        <div style={{ marginTop: 12, ...styles.emptyCard }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ marginTop: 12, ...styles.emptyCard }}>No records for current filter.</div>
      ) : isMobile ? (
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          {filtered.map((r) => {
            const mc = Array.isArray(r.media) ? r.media.length : 0;
            return (
              <div key={r.id} style={styles.mobileCard}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, color: COLORS.text }}>{r.full_name || "-"}</div>
                    <div style={{ color: COLORS.muted, fontWeight: 700, fontSize: 12 }}>{r.mobile || ""}</div>
                  </div>
                  <span style={{ ...styles.pill, background: pillBg(r.status), color: pillColor(r.status) }}>
                    {r.status || "pending"}
                  </span>
                </div>

                <div style={{ marginTop: 8, color: COLORS.sub, fontWeight: 700, fontSize: 13, whiteSpace: "pre-wrap" }}>
                  {r.contribution_text || "-"}
                </div>

                <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={styles.mediaChip}>{mc ? `📎 Media: ${mc}` : "No Media"}</span>
                  </div>
                  <button onClick={() => openPreview(r)} style={styles.previewBtn}>
                    Preview
                  </button>
                </div>

                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", gap: 10, color: COLORS.muted, fontWeight: 700, fontSize: 12 }}>
                  <div>{r.content_lang || "-"}</div>
                  <div>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.tableCard}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={styles.th}>Contributor</th>
                  <th style={styles.th}>Text</th>
                  <th style={styles.th}>Lang</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Media</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const media = Array.isArray(r.media) ? r.media : [];
                  const mc = media.length;

                  return (
                    <tr key={r.id} style={styles.row}>
                      <td style={styles.td}>
                        <div style={styles.cellTitle}>{r.full_name || "-"}</div>
                        <div style={styles.cellSub}>{r.mobile || ""}</div>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.cellBody}>{r.contribution_text || "-"}</div>
                      </td>

                      <td style={styles.td}>{r.content_lang || "-"}</td>

                      <td style={styles.td}>
                        <span style={{ ...styles.pill, background: pillBg(r.status), color: pillColor(r.status) }}>
                          {r.status || "pending"}
                        </span>
                      </td>

                      <td style={styles.td}>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</td>

                      <td style={styles.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          {mc === 0 ? (
                            <span style={{ ...styles.mediaChip, background: "#f2f4f7", borderColor: "#eaecf0" }}>No Media</span>
                          ) : (
                            <>
                              {media.slice(0, 3).map((m, idx) => (
                                <span key={(m.id || idx) + "_chip"} style={styles.mediaChip} title={fileNameFromMedia(m)}>
                                  {pickIconForType(m.media_type)} {String(m.media_type || "MEDIA").toUpperCase()}
                                </span>
                              ))}
                              {mc > 3 ? <span style={styles.moreChip}>+{mc - 3}</span> : null}
                            </>
                          )}

                          <button onClick={() => openPreview(r)} style={styles.previewBtn}>
                            Preview
                          </button>
                        </div>

                        {mc ? <div style={styles.mediaHintSmall}>You can play & download from Preview.</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- styles ---------- */

const styles = {
  page: {
    width: "100%",
    maxWidth: 1240,
    margin: "0 auto",
    padding: "14px 14px 22px",
  },

  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: FW.pageTitle,
    color: COLORS.text,
    letterSpacing: "-0.5px",
    lineHeight: 1.1,
  },
  breadcrumb: {
    marginTop: 6,
    color: COLORS.muted,
    fontWeight: FW.muted,
    fontSize: 13,
  },

  heroCard: {
    marginTop: 14,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 12px 34px rgba(16,24,40,0.06)",
  },
  heroGrid: {
    display: "grid",
    gap: 12,
    alignItems: "center",
  },

  personPhoto: {
    width: 54,
    height: 54,
    borderRadius: 16,
    overflow: "hidden",
    background: "#f2f4f7",
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    border: `1px solid ${COLORS.border}`,
  },
  personName: {
    fontSize: 18,
    fontWeight: FW.sectionTitle,
    color: COLORS.text,
    lineHeight: 1.2,
  },
  personSub: { color: COLORS.muted, fontWeight: 700, fontSize: 13 },

  heroControls: {
    marginTop: 10,
    display: "grid",
    gap: 10,
    alignItems: "center",
  },

  select: {
    height: 40,
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    padding: "0 12px",
    fontWeight: 700,
    background: "#fff",
    fontSize: 14,
  },

  linkBox: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    minWidth: 0,
    background: "#f8fafc",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: "7px 10px",
  },
  linkText: {
    fontWeight: 700,
    color: COLORS.sub,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: "1 1 auto",
    fontSize: 13,
  },

  primaryBtn: {
    height: 44,
    padding: "0 14px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "linear-gradient(135deg,#0b5cff,#0a3fff)",
    color: "#fff",
    fontWeight: FW.btn,
    cursor: "pointer",
    boxShadow: "0 16px 34px rgba(11,92,255,0.22)",
  },

  smallBtn: {
    height: 34,
    padding: "0 12px",
    borderRadius: 10,
    border: "1px solid #d0d5dd",
    background: "#fff",
    fontWeight: FW.btn,
    cursor: "pointer",
    flex: "0 0 auto",
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 14,
  },

  sectionRow: { marginTop: 18, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  sectionTitle: { fontSize: 17, fontWeight: 900, color: COLORS.text },

  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1px solid #d0d5dd",
    borderRadius: 12,
    padding: "0 12px",
    height: 40,
    width: 320,
    maxWidth: "100%",
  },
  search: {
    border: "none",
    outline: "none",
    width: "100%",
    fontWeight: 700,
    background: "transparent",
    color: COLORS.text,
    fontSize: 14,
  },

  tableCard: {
    marginTop: 12,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 12px 34px rgba(16,24,40,0.06)",
  },
  row: { borderTop: "1px solid #eef2f7" },

  th: {
    padding: "12px 12px",
    fontWeight: FW.tableHead,
    color: "#475467",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 12px",
    fontSize: 14,
    color: "#0f172a",
    verticalAlign: "top",
  },

  cellTitle: {
    fontWeight: 900,
    color: COLORS.text,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 260,
  },
  cellSub: { color: COLORS.muted, fontWeight: 700, fontSize: 12 },
  cellBody: { fontWeight: 700, color: "#344054", fontSize: 14, lineHeight: 1.35, whiteSpace: "pre-wrap" },

  pill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e6eaf0",
    fontWeight: 900,
    fontSize: 12,
    textTransform: "capitalize",
  },

  emptyCard: {
    padding: 12,
    borderRadius: 14,
    background: "#f8fafc",
    border: "1px solid #eef2f7",
    fontWeight: 900,
    color: COLORS.sub,
  },

  mobileCard: { border: "1px solid #eef2f7", borderRadius: 16, padding: 12, background: "#fff" },

  mediaChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid #e6eaf0",
    background: "#f8fafc",
    fontWeight: 900,
    fontSize: 12,
    color: "#0f172a",
  },
  moreChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 28,
    minWidth: 34,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid #e6eaf0",
    background: "#fff",
    fontWeight: 900,
    fontSize: 12,
    color: "#475467",
  },
  previewBtn: {
    height: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid #d0d5dd",
    background: "#111827",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  mediaHintSmall: {
    marginTop: 6,
    color: COLORS.muted,
    fontWeight: 700,
    fontSize: 12,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.62)",
    zIndex: 9999,
    display: "grid",
    placeItems: "center",
    padding: 14,
  },
  modalCard: {
    width: "min(980px, 98vw)",
    maxHeight: "92vh",
    overflow: "hidden",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.18)",
    boxShadow: "0 28px 80px rgba(0,0,0,0.35)",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    padding: 14,
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
  },
  modalTitle: { fontWeight: 900, fontSize: 16, color: COLORS.text, letterSpacing: "-0.2px" },
  modalSub: { marginTop: 6, fontWeight: 700, fontSize: 12, color: COLORS.muted, display: "flex", alignItems: "center", gap: 8 },
  modalCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  modalBody: {
    padding: 14,
    overflowY: "auto",
    display: "grid",
    gap: 12,
  },
  modalTextBox: {
    border: "1px solid #eef2f7",
    borderRadius: 16,
    padding: 12,
    background: "#fbfdff",
  },
  modalTextTitle: { fontWeight: 900, fontSize: 13, color: COLORS.text },
  modalText: { marginTop: 8, fontWeight: 700, color: COLORS.sub, whiteSpace: "pre-wrap", lineHeight: 1.35 },
  modalMediaBox: {
    border: "1px solid #eef2f7",
    borderRadius: 16,
    padding: 12,
    background: "#fff",
  },
  emptyMedia: { marginTop: 10, fontWeight: 900, color: COLORS.muted },
  mediaGrid: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
  },
  mediaItem: {
    border: "1px solid #eef2f7",
    borderRadius: 16,
    padding: 12,
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
  },
  mediaTop: { display: "grid", gap: 6, marginBottom: 10 },
  mediaName: { fontWeight: 900, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  mediaMeta: { fontWeight: 700, fontSize: 12, color: COLORS.muted },

  previewImage: {
    width: "100%",
    maxHeight: 240,
    objectFit: "cover",
    borderRadius: 14,
    border: "1px solid #eef2f7",
    background: "#f2f4f7",
  },
  previewAudio: {
    width: "100%",
    height: 44,
  },
  previewVideo: {
    width: "100%",
    maxHeight: 240,
    borderRadius: 14,
    border: "1px solid #eef2f7",
    background: "#000",
  },
  linkPreview: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    background: "#fff",
    fontWeight: 900,
    textDecoration: "none",
    color: COLORS.text,
  },

  mediaActions: { marginTop: 10, display: "flex", gap: 10 },
  ghostActionBtn: {
    flex: 1,
    height: 40,
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    background: "#fff",
    fontWeight: 900,
    textDecoration: "none",
    display: "grid",
    placeItems: "center",
    color: COLORS.text,
  },
  primaryActionBtn: {
    flex: 1,
    height: 40,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "linear-gradient(135deg,#0b5cff,#0a3fff)",
    color: "#fff",
    fontWeight: 900,
    textDecoration: "none",
    display: "grid",
    placeItems: "center",
    boxShadow: "0 16px 34px rgba(11,92,255,0.18)",
  },
  mediaHint: { marginTop: 8, fontSize: 12, color: COLORS.muted, fontWeight: 700 },

  modalFooter: {
    padding: 12,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    justifyContent: "flex-end",
    background: "#fff",
  },
  modalDoneBtn: {
    height: 40,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    background: "#111827",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
};
